// ============================================
// Feedback Modal Component
// ============================================

import { useState } from 'preact/hooks';
import { Modal, ModalFooter, Button } from '@/components/ui';
import { showNotification } from '@/state/signals';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function FeedbackModal({ isOpen, onClose }: FeedbackModalProps) {
  const [email, setEmail] = useState('');
  const [feedback, setFeedback] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: Event) => {
    e.preventDefault();
    if (!feedback.trim()) return;

    setIsSubmitting(true);

    try {
      const formData = new URLSearchParams({
        'form-name': 'jlpt-feedback',
        'email': email,
        'feedback': feedback
      });

      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString()
      });

      if (!response.ok) {
        throw new Error('Form submission failed');
      }

      showNotification('Thank you for your feedback!', 'success');
      resetAndClose();
    } catch (err) {
      console.error('Feedback submission error:', err);
      showNotification('Failed to send feedback. Please try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetAndClose = () => {
    setEmail('');
    setFeedback('');
    onClose();
  };

  const handleClose = () => {
    if (!isSubmitting) resetAndClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Share Your Feedback" size="md">
      {/* Hidden form for Netlify detection at build time */}
      <form name="jlpt-feedback" data-netlify="true" data-netlify-honeypot="bot-field" hidden>
        <input type="email" name="email" />
        <textarea name="feedback" />
      </form>

      <form onSubmit={handleSubmit}>
        {/* Honeypot field for spam protection */}
        <input type="hidden" name="bot-field" />
        <input type="hidden" name="form-name" value="jlpt-feedback" />

        <div className="space-y-4">
          <div>
            <label htmlFor="feedback-email" className="block text-sm font-medium text-gray-700 mb-1">
              Email (optional)
            </label>
            <input
              type="email"
              id="feedback-email"
              name="email"
              value={email}
              onInput={(e) => setEmail((e.currentTarget as HTMLInputElement).value)}
              placeholder="your@email.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
              disabled={isSubmitting}
            />
          </div>

          <div>
            <label htmlFor="feedback-text" className="block text-sm font-medium text-gray-700 mb-1">
              Your feedback <span className="text-red-500">*</span>
            </label>
            <textarea
              id="feedback-text"
              name="feedback"
              value={feedback}
              onInput={(e) => setFeedback((e.currentTarget as HTMLTextAreaElement).value)}
              placeholder="How is the spaced repetition system working for you? Any suggestions for improvement?"
              rows={5}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
              disabled={isSubmitting}
            />
          </div>
        </div>

        <ModalFooter>
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={isSubmitting}
            disabled={!feedback.trim()}
          >
            Send Feedback
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
